import { I18n } from "@assets/strings";
import moment from "moment";
import React from "react";
import styled from "styled-components";
import { LightArticle } from "@domain/lms/article/dtos";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";
import { CalendarIconBis, EyeIcon, LanguageIcon, TimeIcon } from "@ui/components/icons";

interface ArticleDetailsProps extends React.HTMLAttributes<HTMLDivElement> {
	article: LightArticle;
	hideLanguage?: boolean;
	color?: string;
	forSuggest?: boolean;
}
export const ArticleDetails: React.FC<ArticleDetailsProps> = ({
	article,
	hideLanguage,
	color = theme.colors.articleDetailsColor,
	forSuggest = false,
	...htmlProps
}) => {
	return (
		<Container forSuggest={forSuggest} color={color} {...htmlProps}>
			{article.duration ? (
				<StyledSubdiv forSuggest={forSuggest} color={color}>
					<TimeIcon color={color} />
					<span>{article.duration} min</span>
				</StyledSubdiv>
			) : null}
			<StyledSubdiv forSuggest={forSuggest} color={color}>
				<EyeIcon color={color} />
				<span>{article.views.count}</span>
				{/*+ article.publicViews*/}
			</StyledSubdiv>
			{!hideLanguage && (
				<StyledSubdiv forSuggest={forSuggest} color={color}>
					<LanguageIcon color={color} />
					<span>{I18n.t(`language.${article.language}`)}</span>
				</StyledSubdiv>
			)}
			{article.createdAt && !forSuggest ? (
				<StyledSubdiv forSuggest={forSuggest} color={color}>
					<CalendarIconBis color={color} />
					<span>{moment(article.createdAt).format("D MMM YYYY")}</span>
				</StyledSubdiv>
			) : null}
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => !["color", "forSuggest"].includes(prop),
})<{ color?: string; forSuggest?: boolean }>`
	display: flex;
	-webkit-flex-direction: row;
	-ms-flex-direction: row;
	flex-direction: row;
	-webkit-box-pack: justify;
	-webkit-justify-content: space-between;
	-ms-flex-pack: justify;
	justify-content: ${({ forSuggest }) => (forSuggest ? "start" : "end")};
	-webkit-align-items: flex-start;
	-webkit-box-align: flex-start;
	-ms-flex-align: flex-start;
	align-items: flex-start;
	-webkit-flex-wrap: no-wrap;
	-ms-flex-wrap: no-wrap;
	flex-wrap: no-wrap;
	${({ theme }) => theme.textStyles.text};
	${gap(18, "row")}
	margin-bottom: ${({ forSuggest }) => (forSuggest ? "12px" : "10px")};
	margin-top: ${({ forSuggest }) => (forSuggest ? "12px" : undefined)};
	margin-left: ${({ forSuggest }) => (forSuggest ? "18px;" : undefined)};

	> div {
		display: flex;
		align-items: center;
		vertical-align: center;
		color: ${({ color, theme }) => (color ? color : theme.colors.articleDetailsColor)};
		${({ forSuggest }) =>
			forSuggest
				? "font-size: 0.8125rem;font-weight: 700;font-style: italic; color:black"
				: "color: #13131a;font-size: 0.8125rem;font-weight: 700;"};

		> span {
			margin-top: 2px;
			color: ${({ color, theme }) => (color ? color : theme.colors.articleDetailsColor)};
			${({ forSuggest }) =>
				forSuggest
					? "font-size: 0.8125rem;font-weight: 700;font-style: italic; color:black"
					: "color: #13131a;font-size: 0.8125rem;font-weight: 700;"};
		}
	}

	svg {
		margin-right: 2px;
	}
`;

export const StyledSubdiv = styled.div.withConfig({
	shouldForwardProp: (prop) => !["color", "forSuggest"].includes(prop),
})<{ color?: string; forSuggest?: boolean }>`
	${({ forSuggest }) => (!forSuggest ? "padding-bottom: 10px;" : undefined)};
	color: ${({ color, theme }) => (color ? color : theme.colors.articleDetailsColor)};
	${({ forSuggest }) =>
		forSuggest ? "font-size: 0.8125rem;font-weight: 700;font-style: italic; color:black" : undefined};
	> span {
		${({ forSuggest }) =>
			forSuggest ? "font-size: 0.8125rem;font-weight: 700;font-style: italic; color:black" : undefined};
	}
`;
